import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, Code2, Download, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import 'katex/dist/katex.min.css';
import 'highlight.js/styles/vs2015.css';

interface MessageFormatterProps {
  content: string;
  isUser?: boolean;
  className?: string;
}

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
// ... (保持不变)

  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="absolute top-3 right-3 p-2 rounded-lg bg-black/20 hover:bg-black/30 text-white/80 hover:text-white transition-all duration-200 opacity-0 group-hover:opacity-100"
      title={copied ? '已复制' : '复制代码'}
    >
      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
    </button>
  );
};

const CodeBlock: React.FC<{ children: string; language?: string; inline?: boolean }> = ({ 
  children, 
  language,
  inline = false 
}) => {
  if (inline) {
    return (
      <code className="px-2 py-1 bg-gray-100 text-gray-800 rounded-md text-sm font-mono border">
        {children}
      </code>
    );
  }

  const detectedLanguage = language || 'text';
  
  return (
    <div 
      className="group relative my-4" // 移除外层div的padding
      data-block="true" // 标记为块级元素
    >
    <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200/50">
      {/* 代码块头部 */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-gray-800 to-gray-900 border-b border-gray-600">
        <div className="flex items-center space-x-3">
          <div className="flex space-x-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <div className="flex items-center space-x-2 text-gray-300">
            <Code2 className="w-4 h-4" />
            <span className="text-sm font-medium">{detectedLanguage}</span>
          </div>
        </div>
        <CopyButton text={children} />
      </div>
      
      {/* 代码内容 */}
      <div className="relative">
        <SyntaxHighlighter
          language={detectedLanguage}
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: '1.5rem',
            fontSize: '14px',
            lineHeight: '1.6',
            background: 'linear-gradient(135deg, #1e1e1e 0%, #252526 100%)',
            fontFamily: '"JetBrains Mono", "Fira Code", "SF Mono", Monaco, Consolas, monospace',
          }}
          showLineNumbers={children.split('\n').length > 5}
          wrapLines={true}
          wrapLongLines={true}
          lineNumberStyle={{
            color: '#6A9955',
            fontSize: '12px',
            paddingRight: '1rem',
            borderRight: '1px solid #404040',
            marginRight: '1rem',
          }}
        >
          {children}
        </SyntaxHighlighter>
      </div>
      </div>
    </div>
  );
};

const TableComponent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="my-6 overflow-hidden rounded-xl shadow-lg border border-gray-200">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          {children}
        </table>
      </div>
    </div>
  );
};

const TableHead: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
      {children}
    </thead>
  );
};

const TableBody: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <tbody className="bg-white divide-y divide-gray-200">
      {children}
    </tbody>
  );
};

const TableRow: React.FC<{ children: React.ReactNode; isHeader?: boolean }> = ({ children, isHeader = false }) => {
  return (
    <tr className={isHeader ? "bg-gradient-to-r from-blue-50 to-indigo-50" : "hover:bg-gray-50 transition-colors duration-150"}>
      {children}
    </tr>
  );
};

const TableCell: React.FC<{ children: React.ReactNode; isHeader?: boolean }> = ({ children, isHeader = false }) => {
  if (isHeader) {
    return (
      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 tracking-wider border-b-2 border-blue-200">
        {children}
      </th>
    );
  }
  
  return (
    <td className="px-6 py-4 text-sm text-gray-700 border-b border-gray-100">
      {children}
    </td>
  );
};

const MathComponent: React.FC<{ children: string; displayMode?: boolean }> = ({ children, displayMode = false }) => {
  return (
    <div className={`my-3 ${displayMode ? 'text-center p-4 bg-blue-50/30 rounded-lg border border-blue-100' : 'inline-block'}`}>
      <span className="math-expression text-lg">
        {children}
      </span>
    </div>
  );
};

const BlockquoteComponent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <blockquote className="my-4 pl-6 py-3 bg-gradient-to-r from-blue-50/50 to-transparent border-l-4 border-blue-400 italic text-gray-700 rounded-r-lg">
      {children}
    </blockquote>
  );
};

const LinkComponent: React.FC<{ href?: string; children: React.ReactNode }> = ({ href, children }) => {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    if (href && (href.startsWith('/') || href.startsWith('#'))) {
      e.preventDefault();
      navigate(href);
    }
  };

  const isInternal = href && (href.startsWith('/') || href.startsWith('#'));

  return (
    <a
      href={href}
      onClick={isInternal ? handleClick : undefined}
      target={isInternal ? undefined : "_blank"}
      rel={isInternal ? undefined : "noopener noreferrer"}
      className="text-blue-600 hover:text-blue-800 underline underline-offset-2 decoration-2 hover:decoration-blue-800 transition-all duration-200 font-medium cursor-pointer"
    >
      {children}
    </a>
  );
};

const ListComponent: React.FC<{ ordered?: boolean; children: React.ReactNode }> = ({ ordered = false, children }) => {
  const Component = ordered ? 'ol' : 'ul';
  return (
    <Component className={`my-3 space-y-1 ${ordered ? 'list-decimal' : 'list-disc'} list-inside pl-4`}>
      {children}
    </Component>
  );
};

const ListItemComponent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <li className="text-gray-700 leading-relaxed py-1">
      {children}
    </li>
  );
};

const HeadingComponent: React.FC<{ level: number; children: React.ReactNode }> = ({ level, children }) => {
  const Component = `h${level}` as keyof JSX.IntrinsicElements;
  const sizes = {
    1: 'text-2xl font-bold text-gray-900 mt-6 mb-4 pb-2 border-b-2 border-blue-200',
    2: 'text-xl font-bold text-gray-800 mt-5 mb-3 pb-1 border-b border-gray-200',
    3: 'text-lg font-semibold text-gray-800 mt-4 mb-2',
    4: 'text-base font-semibold text-gray-700 mt-3 mb-2',
    5: 'text-sm font-semibold text-gray-700 mt-2 mb-1',
    6: 'text-sm font-medium text-gray-600 mt-2 mb-1'
  };

  return (
    <Component className={sizes[level as keyof typeof sizes] || sizes[3]}>
      <span className="flex items-center">
        <span className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full mr-3"></span>
        {children}
      </span>
    </Component>
  );
};

// 新增PComponent
const PComponent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const hasBlockElement = React.Children.toArray(children).some(child => 
    React.isValidElement(child) && 
    (child.type === 'div' || child.props?.['data-block'])
  );

  return hasBlockElement ? (
    <div className="my-3">{children}</div> // 用div替代p
  ) : (
    <p className="my-3 text-gray-700 leading-relaxed">{children}</p>
  );
};

// 清理AI响应中的特殊标记
const cleanAIResponse = (content: string): string => {
  if (!content) return '';
  
  // 移除 ---RELATED_QUESTIONS--- 及其后的内容（如果它是作为分隔符的话）
  let cleaned = content.replace(/---RELATED_QUESTIONS---[\s\S]*$/gi, '');
  
  // 移除其他可能的标记
  cleaned = cleaned.replace(/---[A-Z_]+---/g, '');
  
  // 移除末尾的多余换行和空白
  cleaned = cleaned.trim();
  
  return cleaned;
};

export const MessageFormatter: React.FC<MessageFormatterProps> = ({ 
  content, 
  isUser = false, 
  className = '' 
}) => {
  // 清理内容中的特殊标记
  const cleanedContent = isUser ? content : cleanAIResponse(content);
  
  // 如果清理后内容为空，显示占位符
  if (!cleanedContent) {
    return null;
  }
  
  return (
    <div className={`message-content prose max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkMath, remarkGfm]}
        rehypePlugins={[rehypeKatex, rehypeHighlight]}
        components={{
          code: ({ node, inline, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            
            return !inline ? (
              <CodeBlock language={language}>
                {String(children).replace(/\n$/, '')}
              </CodeBlock>
            ) : (
              <CodeBlock inline={true}>
                {String(children)}
              </CodeBlock>
            );
          },
          table: ({ children }) => <TableComponent>{children}</TableComponent>,
          thead: ({ children }) => <TableHead>{children}</TableHead>,
          tbody: ({ children }) => <TableBody>{children}</TableBody>,
          tr: ({ children }) => <TableRow>{children}</TableRow>,
          th: ({ children }) => <TableCell isHeader={true}>{children}</TableCell>,
          td: ({ children }) => <TableCell>{children}</TableCell>,
          blockquote: ({ children }) => <BlockquoteComponent>{children}</BlockquoteComponent>,
          a: ({ href, children }) => <LinkComponent href={href}>{children}</LinkComponent>,
          ul: ({ children }) => <ListComponent>{children}</ListComponent>,
          ol: ({ children }) => <ListComponent ordered={true}>{children}</ListComponent>,
          li: ({ children }) => <ListItemComponent>{children}</ListItemComponent>,
          h1: ({ children }) => <HeadingComponent level={1}>{children}</HeadingComponent>,
          h2: ({ children }) => <HeadingComponent level={2}>{children}</HeadingComponent>,
          h3: ({ children }) => <HeadingComponent level={3}>{children}</HeadingComponent>,
          h4: ({ children }) => <HeadingComponent level={4}>{children}</HeadingComponent>,
          h5: ({ children }) => <HeadingComponent level={5}>{children}</HeadingComponent>,
          h6: ({ children }) => <HeadingComponent level={6}>{children}</HeadingComponent>,
          p: PComponent,
          strong: ({ children }) => (
            <strong className="font-semibold text-gray-900">
              {children}
            </strong>
          ),
          em: ({ children }) => (
            <em className="italic text-gray-800">
              {children}
            </em>
          ),
          hr: () => (
            <hr className="my-6 border-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
          ),
        }}
      >
        {cleanedContent}
      </ReactMarkdown>
    </div>
  );
}; 